import { NavigationGuardProvider } from "next-navigation-guard"
import React from "react"
import App from "next/app"
import Head from "next/head"
import ErrorBoundary from "../components/layouts/ErrorBoundary"
import { NotificationDetailModalPortal } from "../components/uiKit/Notification/Notification"
import { getInitialAppProps, reduxWrapper } from "../components/redux/setup"
import { urlChanged } from "../components/router/actions"
import { Router } from "../server/router"
import { gaPageView } from "../utils/analytics"
import { isApiKeySet, setApiKey } from "../utils/apiKeys"
import { isBrowser } from "../utils/browser"
import { cookieNames, getCookie, sidebarCollapsedCookie } from "../utils/cookies"
import { getDispatch } from "../utils/redux"
import type { NextContext } from "../utils/types"
// Dont change order of stylesheets. Change can broke UI
import "../styles/tailwind-init.css"
import "react-dates/lib/css/_datepicker.css"
import "../styles/lib/app.less"
import "../styles/global.css"
import "../styles/lib/react-crop.css"
import { getServerRuntimeConfig } from "../utils/runtimeConfig"
import { CrashTest } from "../components/common/CrashTest"
import { CurrentPathContext, SidebarCollapsedContext } from "../components/layouts/CurrentPathContext"

class MyApp extends App {
    static getInitialProps = getInitialAppProps(store => async appContext => {
        // @ts-expect-error
        const ctx: NextContext = appContext.ctx

        const sessionCookie = getCookie(cookieNames.session, ctx.req)
        const isAuthenticated = !!sessionCookie

        if (pathRequiresAuth(ctx.pathname) && !isAuthenticated) {
            redirectToSignIn(ctx)
        }

        // Generate api key
        const serverRuntimeConfig = getServerRuntimeConfig().serverRuntimeConfig
        let apiKey
        if (!isBrowser) {
            const { defaultApiKeyGenerator } = await import("@figurepos/fgr-lib-backend/lib/express/apiKey/middleware")
            apiKey = defaultApiKeyGenerator.generate(appContext.ctx.req, serverRuntimeConfig.apiSecretKey, true)
        }

        store.dispatch(urlChanged(ctx.query, ctx.asPath, ctx.pathname))
        const appProps = await App.getInitialProps(appContext)

        return {
            ...appProps,
            apiKey: apiKey,
            currentPath: ctx.req?.url || ctx.asPath || "/",
            sidebarCollapsed: getCookie(sidebarCollapsedCookie, ctx.req) === "true",
        }
    })

    componentDidMount = () => {
        this.setRouterState()
        Router.router?.events?.on("routeChangeComplete", this.onRouteChangeComplete)
    }
    componentWillUnmount = () => {
        Router.router?.events?.off("routeChangeComplete", this.onRouteChangeComplete)
    }

    onRouteChangeComplete = url => {
        this.setRouterState()
        gaPageView(url)
    }

    setRouterState = () => {
        const dispatch = getDispatch()
        dispatch && dispatch(urlChanged(Router.router?.query, Router.router?.asPath, Router.router?.pathname))
    }

    render() {
        const { Component, pageProps } = this.props

        // Set API key before children render/mount so it's available for API calls
        // triggered in children's componentDidMount
        if (this.props.apiKey && !isApiKeySet()) {
            setApiKey(this.props.apiKey)
        }

        return (
            <>
                <Head>
                    <title>Figure Manage</title>
                    <meta name="viewport" key="viewport" content="initial-scale=0" />
                </Head>
                <ErrorBoundary>
                    <CrashTest />
                    <NavigationGuardProvider>
                        <CurrentPathContext.Provider value={this.props.currentPath}>
                            <SidebarCollapsedContext.Provider value={this.props.sidebarCollapsed}>
                                <Component {...pageProps} />
                            </SidebarCollapsedContext.Provider>
                        </CurrentPathContext.Provider>
                    </NavigationGuardProvider>
                    <NotificationDetailModalPortal />
                </ErrorBoundary>
            </>
        )
    }
}

const pathRequiresAuth = pathname => {
    return (
        pathname !== "/app/login" &&
        pathname !== "/_next" &&
        pathname !== "/app/passwordResetUser" &&
        pathname !== "/app/signUp" &&
        pathname !== "/app/passwordResetRequest" &&
        pathname !== "/app/onlineReceipt/onlineReceipt" &&
        pathname !== "/app/onlineReceipt/feedback" &&
        pathname !== "/app/onlineReceipt/feedback_v2" &&
        pathname !== "/app/giftCard/detail"
    )
}

const redirectToSignIn = (context: NextContext) => {
    const { res } = context

    if (res) {
        res.writeHead(302, {
            Location: "/login",
        })
        res.end()
    } else {
        Router.replace("/login")
    }
}

export default reduxWrapper.withRedux(MyApp)
